class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) {
      overlay.addEventListener('click', this.close.bind(this));
    }
    
    // Set up cart icon accessibility with a small delay to ensure DOM is ready
    setTimeout(() => {
      this.setHeaderCartIconAccessibility();
    }, 50);
    
    // Add state management properties
    this.isReloading = false;
    this.reloadCooldown = 60 * 60 * 1000; // 1 hour in milliseconds
  }

  // Check if cart reload is allowed (once per hour)
  canReloadCart() {
    const lastReloadTime = localStorage.getItem('cartDrawer_lastReload');
    if (!lastReloadTime) {
      return true; // First time, allow reload
    }
    
    const timeSinceLastReload = Date.now() - parseInt(lastReloadTime);
    return timeSinceLastReload >= this.reloadCooldown;
  }

  // Mark cart reload as used
  markCartReloaded() {
    localStorage.setItem('cartDrawer_lastReload', Date.now().toString());
    console.log('[CartDrawer] Cart reload marked in localStorage');
  }

  // Get time until next allowed reload
  getTimeUntilNextReload() {
    const lastReloadTime = localStorage.getItem('cartDrawer_lastReload');
    if (!lastReloadTime) {
      return 0;
    }
    
    const timeSinceLastReload = Date.now() - parseInt(lastReloadTime);
    const timeRemaining = this.reloadCooldown - timeSinceLastReload;
    return Math.max(0, timeRemaining);
  }

  // Add state validation method with localStorage tracking
  validateCartState() {
    // Prevent validation during reload or re-rendering
    if (this.isReloading || this.isReRendering) {
      return true;
    }
    
    // Only validate if cart drawer is active/visible
    if (!this.classList.contains('active')) {
      return true;
    }
    
    const drawerInnerEmpty = this.querySelector('.drawer__inner-empty');
    const drawerWrapper = this.querySelector('.drawer__wrapper');
    const cartItems = this.querySelectorAll('.cart-item, [data-cart-item]');
    const cartHasItems = cartItems.length > 0;
    
    // Helper function to check if element is actually visible
    const isElementVisible = (element) => {
      if (!element) return false;
      
      // Check computed style
      const computedStyle = window.getComputedStyle(element);
      const isDisplayNone = computedStyle.display === 'none';
      const isVisibilityHidden = computedStyle.visibility === 'hidden';
      const isOpacityZero = parseFloat(computedStyle.opacity) === 0;
      
      // Check if element has hidden class
      const hasHiddenClass = element.classList.contains('hidden') || 
                            element.classList.contains('visually-hidden') ||
                            element.classList.contains('sr-only');
      
      // Check if element is in viewport
      const rect = element.getBoundingClientRect();
      const isInViewport = rect.width > 0 && rect.height > 0;
      
      return !isDisplayNone && !isVisibilityHidden && !isOpacityZero && !hasHiddenClass && isInViewport;
    };
    
    const emptyIsVisible = isElementVisible(drawerInnerEmpty);
    const wrapperIsVisible = isElementVisible(drawerWrapper);
    
    // Debug logging
    console.log('[CartDrawer] State validation:', {
      hasEmptyElement: !!drawerInnerEmpty,
      hasWrapper: !!drawerWrapper,
      hasItems: cartHasItems,
      isEmptyClass: this.classList.contains('is-empty'),
      emptyIsVisible,
      wrapperIsVisible,
      emptyContentVisible: isElementVisible(drawerInnerEmpty ? drawerInnerEmpty.querySelector('.cart-drawer__warnings') : null),
      wrapperContentVisible: isElementVisible(drawerWrapper ? drawerWrapper.querySelector('.drawer__contents') : null),
      emptyDisplay: drawerInnerEmpty ? window.getComputedStyle(drawerInnerEmpty).display : 'N/A',
      wrapperDisplay: drawerWrapper ? window.getComputedStyle(drawerWrapper).display : 'N/A',
      emptyClasses: drawerInnerEmpty ? drawerInnerEmpty.className : 'N/A',
      wrapperClasses: drawerWrapper ? drawerWrapper.className : 'N/A'
    });
    
    // Check for inconsistent state: both empty and wrapper visible
    // The issue is that both elements are always in DOM, so we need to check their content visibility
    const emptyContent = drawerInnerEmpty ? drawerInnerEmpty.querySelector('.cart-drawer__warnings') : null;
    const wrapperContent = drawerWrapper ? drawerWrapper.querySelector('.drawer__contents') : null;
    
    const emptyContentVisible = isElementVisible(emptyContent);
    const wrapperContentVisible = isElementVisible(wrapperContent);
    
    if (emptyContentVisible && wrapperContentVisible) {
      console.warn('[CartDrawer] Inconsistent state detected: both empty content and wrapper content are visible');
      
      if (this.canReloadCart()) {
        this.reloadCart('inconsistent_state_both_content_visible');
      } else {
        const timeRemaining = this.getTimeUntilNextReload();
        console.warn(`[CartDrawer] Cart reload blocked - next allowed in ${Math.ceil(timeRemaining / 60000)} minutes`);
      }
      return false;
    }
    
    // Check for empty state inconsistency
    if (this.classList.contains('is-empty') && cartHasItems) {
      console.warn('[CartDrawer] Inconsistent state detected: cart has items but is marked as empty');
      
      if (this.canReloadCart()) {
        this.reloadCart('inconsistent_state_has_items_but_empty');
      } else {
        const timeRemaining = this.getTimeUntilNextReload();
        console.warn(`[CartDrawer] Cart reload blocked - next allowed in ${Math.ceil(timeRemaining / 60000)} minutes`);
      }
      return false;
    }
    
    // Check for non-empty state inconsistency
    if (!this.classList.contains('is-empty') && !cartHasItems && drawerInnerEmpty) {
      console.warn('[CartDrawer] Inconsistent state detected: cart is empty but not marked as empty');
      
      if (this.canReloadCart()) {
        this.reloadCart('inconsistent_state_empty_but_not_marked');
      } else {
        const timeRemaining = this.getTimeUntilNextReload();
        console.warn(`[CartDrawer] Cart reload blocked - next allowed in ${Math.ceil(timeRemaining / 60000)} minutes`);
      }
      return false;
    }
    
    return true;
  }

  // Add cart reload method with localStorage tracking
  async reloadCart(reason = 'unknown') {
    // Prevent multiple simultaneous reloads
    if (this.isReloading) {
      console.log('[CartDrawer] Cart reload already in progress, skipping...');
      return;
    }
    
    // Check if reload is allowed
    if (!this.canReloadCart()) {
      const timeRemaining = this.getTimeUntilNextReload();
      console.warn(`[CartDrawer] Cart reload blocked - next allowed in ${Math.ceil(timeRemaining / 60000)} minutes`);
      return;
    }
    
    this.isReloading = true;
    console.log(`[CartDrawer] Reloading cart due to: ${reason}`);
    
    try {
      // Mark reload as used immediately
      this.markCartReloaded();
      
      // Fetch current cart state from Shopify
      const response = await fetch('/cart.js');
      if (!response.ok) {
        throw new Error(`Failed to fetch cart: ${response.status}`);
      }
      
      const cartData = await response.json();
      
      // Fetch cart drawer sections
      const sectionsResponse = await fetch(`?sections=cart-drawer,cart-icon-bubble&section_id=cart-drawer`);
      if (!sectionsResponse.ok) {
        throw new Error(`Failed to fetch sections: ${sectionsResponse.status}`);
      }
      
      const sectionsData = await sectionsResponse.json();
      
      // Create a parsed state object similar to what Shopify returns
      const parsedState = {
        ...cartData,
        sections: sectionsData
      };
      
      // Re-render the cart drawer with fresh data
      this.renderContents(parsedState);
      
      console.log('[CartDrawer] Cart reloaded successfully');
    } catch (error) {
      console.error('[CartDrawer] Failed to reload cart:', error);
      
      // Fallback: force page reload if cart reload fails
      console.warn('[CartDrawer] Falling back to page reload');
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    } finally {
      // Reset reloading flag after a delay to prevent immediate re-validation
      setTimeout(() => {
        this.isReloading = false;
      }, 1000);
    }
  }

  // Manual trigger method for cart reload (can be called from console or other scripts)
  forceCartReload() {
    console.log('[CartDrawer] Manual cart reload triggered');
    this.reloadCart('manual_trigger');
  }

  // Debug method to check reload status
  getReloadStatus() {
    const canReload = this.canReloadCart();
    const timeRemaining = this.getTimeUntilNextReload();
    const lastReload = localStorage.getItem('cartDrawer_lastReload');
    
    return {
      canReload,
      timeRemaining: Math.ceil(timeRemaining / 60000), // in minutes
      lastReload: lastReload ? new Date(parseInt(lastReload)).toISOString() : 'Never'
    };
  }

  // Manual validation trigger for debugging
  debugValidateState() {
    console.log('[CartDrawer] Manual state validation triggered');
    this.validateCartState();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) {
      // Retry after a short delay in case the DOM is not ready
      setTimeout(() => this.setHeaderCartIconAccessibility(), 100);
      return;
    }
    
    // Remove any existing listeners to prevent duplicates
    cartLink.removeEventListener('click', this.handleCartIconClick);
    cartLink.removeEventListener('keydown', this.handleCartIconKeydown);
    
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    
    // Store bound event handlers
    this.handleCartIconClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      this.open(cartLink);
    };
    
    this.handleCartIconKeydown = (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    };
    
    cartLink.addEventListener('click', this.handleCartIconClick);
    cartLink.addEventListener('keydown', this.handleCartIconKeydown);
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        
        // Only trap focus if the container exists
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
        
        // Only trigger cart drawer opened event if it's not a re-render and not already opened
        if (!this.isReRendering && !this.wasOpened) {
          this.wasOpened = true;
          document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
        }
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    this.wasOpened = false;
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    // console.log('[CartDrawer] renderContents called with:', parsedState);
    if (parsedState.sections) {
      // console.log('[CartDrawer] Section keys:', Object.keys(parsedState.sections));
    } else {
      console.warn('[CartDrawer] No sections found in parsedState');
    }

    const drawerInner = this.querySelector('.drawer__inner');
    // console.log('[CartDrawer] Expect .drawer__inner to exist:', !!drawerInner, drawerInner);
    if (drawerInner && drawerInner.classList.contains('is-empty')) {
      drawerInner.classList.remove('is-empty');
      // console.log('[CartDrawer] Removed is-empty class from .drawer__inner');
    }

    this.productId = parsedState.id;
    // console.log('[CartDrawer] Set productId:', this.productId);

    // Mark as re-rendering to prevent unnecessary recommendations loading
    this.isReRendering = true;
    // console.log('[CartDrawer] Set isReRendering to true');

    // Preserve recommendations container before re-rendering
    const recommendationsContainer = this.querySelector('#shopify-section-cart-drawer-recommendations');
    // console.log('[CartDrawer] Expect #shopify-section-cart-drawer-recommendations to exist:', !!recommendationsContainer, recommendationsContainer);
    const recommendationsHTML = recommendationsContainer ? recommendationsContainer.outerHTML : null;
    // console.log('[CartDrawer] recommendationsHTML:', !!recommendationsHTML, recommendationsHTML);

    // Check if cart has items - handle both single item and full cart object
    const cartHasItems = parsedState.item_count > 0 || (parsedState.items && parsedState.items.length > 0) || !!parsedState.product_id;
    // console.log('[CartDrawer] cartHasItems:', cartHasItems);

    // Remove is-empty from the cart drawer root if cart is not empty
    if (cartHasItems && this.classList.contains('is-empty')) {
      this.classList.remove('is-empty');
      // console.log('[CartDrawer] Removed is-empty class from <cart-drawer>');
    }

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      // console.log(`[CartDrawer] Expect sectionElement for id=${section.id}, selector=${section.selector}:`, !!sectionElement, sectionElement);

      const sectionHTML = parsedState.sections && parsedState.sections[section.id]
        ? this.getSectionInnerHTML(parsedState.sections[section.id], section.selector)
        : null;
      // console.log(`[CartDrawer] Expect sectionHTML for id=${section.id}:`, !!sectionHTML, sectionHTML);

      if (!sectionElement) {
        console.warn(`[CartDrawer] Section element not found for id=${section.id}`);
        return;
      }
      
      if (sectionHTML) {
        // If this is the cart drawer section, preserve the recommendations container
        if (section.id === 'cart-drawer') {
          const tempDiv = document.createElement('div');
          tempDiv.innerHTML = sectionHTML;
          // console.log('[CartDrawer] tempDiv created for cart-drawer section:', tempDiv);
          
          // Find the recommendations section in the new HTML
          const newRecommendationsSection = tempDiv.querySelector('#shopify-section-cart-drawer-recommendations');
          // console.log('[CartDrawer] Expect newRecommendationsSection in tempDiv:', !!newRecommendationsSection, newRecommendationsSection);
          
          // Always preserve the recommendations container if it exists
          if (recommendationsHTML && newRecommendationsSection) {
            newRecommendationsSection.outerHTML = recommendationsHTML;
            // console.log('[CartDrawer] Restored recommendations container');
          }
          
          sectionElement.innerHTML = tempDiv.innerHTML;
          // console.log('[CartDrawer] Updated cart-drawer section innerHTML:', sectionElement.innerHTML);
        } else {
          sectionElement.innerHTML = sectionHTML;
          // console.log(`[CartDrawer] Updated section innerHTML for id=${section.id}:`, sectionElement.innerHTML);
        }
      }
    });

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      // console.log('[CartDrawer] Expect #CartDrawer-Overlay to exist:', !!overlay, overlay);
      if (overlay) {
        overlay.addEventListener('click', this.close.bind(this));
        // console.log('[CartDrawer] Added click event to overlay');
      }
      // console.log('[CartDrawer] this.open exists:', !!this.open, this.open);
      this.open();
      // console.log('[CartDrawer] Drawer opened');
      
      // Reset re-rendering flag
      this.isReRendering = false;
      // console.log('[CartDrawer] Set isReRendering to false');
      
      // Validate cart state after rendering is complete
      setTimeout(() => {
        this.validateCartState();
      }, 100);
      
      // Trigger cart drawer rendered event for recommendations
      document.dispatchEvent(new CustomEvent('cart-drawer:rendered', {
        detail: { cartData: parsedState }
      }));
      // console.log('[CartDrawer] cart-drawer:rendered event dispatched');
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const sectionElement = parsed.querySelector(selector);
    return sectionElement ? sectionElement.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

// Define the cart drawer custom element
try {
  customElements.define('cart-drawer', CartDrawer);
} catch (error) {
  // Custom element definition failed
}

// Also set up a fallback for cart icon clicks in case the custom element doesn't work
document.addEventListener('DOMContentLoaded', () => {
  // Find cart drawer element
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer) {
    // Set up fallback cart icon click handler
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) {
      cartLink.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (cartDrawer.open) {
          cartDrawer.open(cartLink);
        }
      });
    }
  }
});

// Wait for CartItems to be available before defining CartDrawerItems
function defineCartDrawerItems() {
  if (typeof CartItems === 'undefined') {
    // If CartItems is not available yet, wait a bit and try again
    setTimeout(defineCartDrawerItems, 50);
    return;
  }

  class CartDrawerItems extends CartItems {
    getSectionsToRender() {
      return [
        {
          id: 'CartDrawer',
          section: 'cart-drawer',
          selector: '.drawer__inner',
        },
        {
          id: 'cart-icon-bubble',
          section: 'cart-icon-bubble',
          selector: '.shopify-section',
        },
      ];
    }

    renderContents(parsedState) {
      // Preserve recommendations container before re-rendering
      const cartDrawer = document.querySelector('cart-drawer');
      const recommendationsContainer = cartDrawer?.querySelector('#shopify-section-cart-drawer-recommendations');
      const recommendationsHTML = recommendationsContainer ? recommendationsContainer.outerHTML : null;
      
      // Check if cart has items - handle both single item and full cart object
      const cartHasItems = parsedState.item_count > 0 || (parsedState.items && parsedState.items.length > 0) || !!parsedState.product_id;
      
      // Call the parent class renderContents method
      super.renderContents(parsedState);
      
      // Always restore recommendations if they were preserved
      if (recommendationsHTML && cartDrawer) {
        const newRecommendationsContainer = cartDrawer.querySelector('#shopify-section-cart-drawer-recommendations');
        if (newRecommendationsContainer) {
          newRecommendationsContainer.outerHTML = recommendationsHTML;
        }
      }
      
      // Trigger cart drawer rendered event for recommendations
      setTimeout(() => {
        document.dispatchEvent(new CustomEvent('cart-drawer:rendered', {
          detail: { cartData: parsedState }
        }));
      }, 50);
    }
  }

  customElements.define('cart-drawer-items', CartDrawerItems);
}

// Start the process of defining CartDrawerItems
defineCartDrawerItems();
